const LINE_RE = /^- (?<role>\S+)(?: "(?<name>[^"]*)")?(?: \(h\d\))?(?: \[(?<ref>e\d+)\])?(?: testid=(?<testid>\S+))?(?: value=(?<value>"(?:[^"\\]|\\.)*"))?(?<states>(?: [a-z]+)*)\s*$/;
export function parseSnapshot(text) {
    const nodes = [];
    for (const raw of text.split("\n")) {
        const line = raw.trim();
        if (!line.startsWith("- "))
            continue;
        const m = LINE_RE.exec(line);
        if (!m?.groups)
            continue;
        const g = m.groups;
        nodes.push({
            role: g.role,
            name: g.name,
            ref: g.ref,
            testid: g.testid,
            value: g.value ? JSON.parse(g.value) : undefined,
            states: (g.states ?? "").trim().split(/\s+/).filter(Boolean),
        });
    }
    return nodes;
}
const FILLABLE = new Set(["textbox", "searchbox", "spinbutton"]);
let callSeq = 0;
const id = () => `c${++callSeq}`;
/**
 * A deterministic, model-free planner used for demos and offline verification.
 * It fills mapped form fields from a profile and then clicks a submit target,
 * exercising the full read → type → select → gated-click → confirm pipeline.
 */
export class HeuristicPlanner {
    config;
    /** Fields already handled, so we advance instead of looping. */
    handled = new Set();
    clickedSubmit = false;
    constructor(config) {
        this.config = config;
    }
    key(n) {
        if (n.testid && n.testid in this.config.profile)
            return n.testid;
        const nm = n.name?.toLowerCase();
        if (nm && nm in this.config.profile)
            return nm;
        return undefined;
    }
    async next(ctx) {
        const nodes = parseSnapshot(ctx.observation);
        // 1. Fill the next unfilled, mapped text field.
        for (const n of nodes) {
            if (!n.ref || !FILLABLE.has(n.role))
                continue;
            const k = this.key(n);
            if (!k || this.handled.has(k))
                continue;
            if (n.value && n.value.length > 0) {
                this.handled.add(k);
                continue;
            }
            this.handled.add(k);
            return {
                text: `Filling ${n.name ?? k}.`,
                toolCalls: [{ id: id(), name: "type", input: { ref: n.ref, text: this.config.profile[k] } }],
                done: false,
            };
        }
        // 2. Set the next unfilled, mapped select.
        for (const n of nodes) {
            if (!n.ref || n.role !== "combobox")
                continue;
            const k = this.key(n);
            if (!k || this.handled.has(k))
                continue;
            this.handled.add(k);
            return {
                text: `Choosing ${n.name ?? k}.`,
                toolCalls: [{ id: id(), name: "select", input: { ref: n.ref, option: { label: this.config.profile[k] } } }],
                done: false,
            };
        }
        // 3. Click the submit target (gated by the safety layer).
        if (!this.clickedSubmit && this.config.submit) {
            const target = nodes.find((n) => n.ref &&
                ((this.config.submit.testid && n.testid === this.config.submit.testid) ||
                    (this.config.submit.name &&
                        n.name?.toLowerCase().includes(this.config.submit.name.toLowerCase()))));
            if (target?.ref) {
                this.clickedSubmit = true;
                return {
                    text: `Everything looks filled in — submitting.`,
                    toolCalls: [{ id: id(), name: "click", input: { ref: target.ref } }],
                    done: true,
                };
            }
        }
        return { text: "Nothing left to do.", toolCalls: [], done: true };
    }
}
/**
 * Talks to a Quill server proxy that runs the model via the AI SDK and returns
 * the next planner step. Keys stay server-side. See packages/server.
 */
export class RemotePlanner {
    endpoint;
    init;
    constructor(endpoint, init) {
        this.endpoint = endpoint;
        this.init = init;
    }
    async next(ctx) {
        const res = await fetch(this.endpoint, {
            method: "POST",
            headers: { "content-type": "application/json", ...(this.init?.headers ?? {}) },
            body: JSON.stringify({
                task: ctx.task,
                history: sanitize(ctx.history),
                observation: ctx.observation,
                step: ctx.step,
            }),
        });
        if (!res.ok)
            throw new Error(`planner endpoint ${res.status}`);
        const data = (await res.json());
        return { text: data.text, toolCalls: data.toolCalls ?? [], done: Boolean(data.done) };
    }
}
function sanitize(history) {
    // Cap tool-result payloads so the request stays small.
    return history.map((m) => m.role === "tool" && m.content.length > 2000
        ? { ...m, content: m.content.slice(0, 2000) + "…" }
        : m);
}
//# sourceMappingURL=planners.js.map